import { resolveQuestGeometryRevision } from 'lib/questGeometryFrame'
import {
    buildStreamQuestRowRef,
    streamQuestRowRefsEqual,
    streamQuestRowKey,
    type StreamQuestRowRef,
} from 'refactor/hooks/api/streamQuestRows'
import type { Quest } from 'types/quests'
import queryClient from 'api/queryClient'

export const NATURAL_FEED_MAX_SESSIONS = 6
export const NATURAL_FEED_MAX_ROWS_PER_SESSION = 5000
export const NATURAL_FEED_MAX_GEOMETRY_ENTRIES_PER_SESSION = 2500

export type NaturalFeedQuestDescriptor = {
    geometryRevision: string
    row: StreamQuestRowRef
    rowKey: string
}

export type NaturalFeedSessionSnapshot = {
    atCapacity: boolean
    pinnedQuest: NaturalFeedQuestDescriptor | null
    revision: number
    rows: NaturalFeedQuestDescriptor[]
}

type SegmentGeometry = {
    height: number
    layoutSignature: string
}

type NaturalFeedSession = {
    geometry: Map<string, SegmentGeometry>
    lastAccessedAt: number
    pages: Map<number, NaturalFeedQuestDescriptor[]>
    resetOnNextFirstPage: boolean
    snapshot: NaturalFeedSessionSnapshot
}

const EMPTY_SNAPSHOT: NaturalFeedSessionSnapshot = {
    atCapacity: false,
    pinnedQuest: null,
    revision: 0,
    rows: [],
}
const sessions = new Map<string, NaturalFeedSession>()
const listenersBySession = new Map<string, Set<() => void>>()
let accessSequence = 0

function nextAccessSequence() {
    accessSequence += 1
    return accessSequence
}

function createSession(): NaturalFeedSession {
    return {
        geometry: new Map(),
        lastAccessedAt: nextAccessSequence(),
        pages: new Map(),
        resetOnNextFirstPage: false,
        snapshot: EMPTY_SNAPSHOT,
    }
}

function notifySession(sessionKey: string) {
    listenersBySession.get(sessionKey)?.forEach(listener => listener())
}

function evictOldSessions(activeSessionKey?: string) {
    if (sessions.size <= NATURAL_FEED_MAX_SESSIONS) return

    const candidates = Array.from(sessions.entries())
        .filter(
            ([sessionKey]) =>
                sessionKey !== activeSessionKey &&
                (listenersBySession.get(sessionKey)?.size || 0) === 0 &&
                Number(
                    queryClient
                        .getQueryCache()
                        .find({ queryKey: ['quests', sessionKey], exact: true })
                        ?.getObserversCount?.() || 0,
                ) === 0,
        )
        .sort(([, left], [, right]) => left.lastAccessedAt - right.lastAccessedAt)

    while (sessions.size > NATURAL_FEED_MAX_SESSIONS && candidates.length > 0) {
        const [sessionKey] = candidates.shift()!
        sessions.delete(sessionKey)
        queryClient.removeQueries({ queryKey: ['quests', sessionKey], exact: true })
        notifySession(sessionKey)
    }
}

function descriptorsEqual(
    left: NaturalFeedQuestDescriptor | null | undefined,
    right: NaturalFeedQuestDescriptor | null | undefined,
) {
    if (left === right) return true
    if (!left || !right) return false
    if (left.rowKey !== right.rowKey || left.geometryRevision !== right.geometryRevision)
        return false

    return streamQuestRowRefsEqual(left.row, right.row)
}

function descriptorArraysEqual(
    left: NaturalFeedQuestDescriptor[],
    right: NaturalFeedQuestDescriptor[],
) {
    return (
        left.length === right.length &&
        left.every((descriptor, index) => descriptorsEqual(descriptor, right[index]))
    )
}

function refreshPageDescriptorsWithoutReordering(
    previous: NaturalFeedQuestDescriptor[],
    incoming: NaturalFeedQuestDescriptor[],
) {
    if (previous.length === 0) return incoming
    const incomingByKey = new Map(incoming.map(descriptor => [descriptor.rowKey, descriptor]))
    return previous.map(descriptor => incomingByKey.get(descriptor.rowKey) || descriptor)
}

function flattenSessionRows(
    session: NaturalFeedSession,
    pinnedQuest: NaturalFeedQuestDescriptor | null,
): NaturalFeedQuestDescriptor[] {
    const seen = new Set<string>()
    const rows: NaturalFeedQuestDescriptor[] = []
    const pinnedQuestId = pinnedQuest?.row?.id == null ? null : String(pinnedQuest.row.id)

    Array.from(session.pages.entries())
        .sort(([leftPage], [rightPage]) => leftPage - rightPage)
        .forEach(([, pageRows]) => {
            pageRows.forEach(descriptor => {
                if (
                    pinnedQuestId != null &&
                    descriptor.row?.id != null &&
                    String(descriptor.row.id) === pinnedQuestId
                )
                    return
                if (seen.has(descriptor.rowKey)) return
                seen.add(descriptor.rowKey)
                rows.push(descriptor)
            })
        })

    return rows.slice(0, NATURAL_FEED_MAX_ROWS_PER_SESSION)
}

function trimSessionPages(session: NaturalFeedSession) {
    let remainingRows = NATURAL_FEED_MAX_ROWS_PER_SESSION
    Array.from(session.pages.entries())
        .sort(([leftPage], [rightPage]) => leftPage - rightPage)
        .forEach(([page, pageRows]) => {
            if (remainingRows <= 0) {
                session.pages.delete(page)
                return
            }
            if (pageRows.length > remainingRows) {
                session.pages.set(page, pageRows.slice(0, remainingRows))
            }
            remainingRows -= pageRows.length
        })
}

export function naturalFeedSessionKey(queryKey: readonly unknown[]): string {
    const baseKey = String(queryKey[1] ?? 'default')
    const scopes = queryKey.slice(2)
    return scopes.length > 0 ? JSON.stringify([baseKey, ...scopes]) : baseKey
}

export function buildNaturalFeedQuestDescriptor(quest: Quest): NaturalFeedQuestDescriptor {
    const row = buildStreamQuestRowRef(quest)
    return {
        geometryRevision: resolveQuestGeometryRevision(quest),
        row,
        rowKey: streamQuestRowKey(row),
    }
}

export function recordNaturalFeedSessionPage({
    page,
    pinnedQuest,
    quests,
    sessionKey,
    replacePageOnRefresh = false,
}: {
    page: number
    pinnedQuest?: Quest | null
    quests: Quest[]
    sessionKey: string
    replacePageOnRefresh?: boolean
}) {
    const session = sessions.get(sessionKey) || createSession()
    const resetApplied = page === 1 && session.resetOnNextFirstPage
    if (resetApplied) {
        session.pages.clear()
        session.resetOnNextFirstPage = false
    }
    const previousPage = session.pages.get(page) || []
    const incomingPage = quests.map(buildNaturalFeedQuestDescriptor)
    const nextPage = replacePageOnRefresh
        ? incomingPage
        : refreshPageDescriptorsWithoutReordering(previousPage, incomingPage)
    const nextPinned = pinnedQuest ? buildNaturalFeedQuestDescriptor(pinnedQuest) : null
    const pinnedQuestChanged =
        page === 1 && !descriptorsEqual(session.snapshot.pinnedQuest, nextPinned)
    const pageChanged = !descriptorArraysEqual(previousPage, nextPage)

    session.lastAccessedAt = nextAccessSequence()
    sessions.set(sessionKey, session)
    evictOldSessions(sessionKey)

    if (!resetApplied && !pageChanged && !pinnedQuestChanged) return session.snapshot

    if (pageChanged) {
        session.pages.set(page, nextPage)
        trimSessionPages(session)
    }
    const pinned = page === 1 ? nextPinned : session.snapshot.pinnedQuest
    const rows = flattenSessionRows(session, pinned)
    session.snapshot = {
        atCapacity: rows.length >= NATURAL_FEED_MAX_ROWS_PER_SESSION,
        pinnedQuest: pinned,
        revision: session.snapshot.revision + 1,
        rows,
    }
    notifySession(sessionKey)
    return session.snapshot
}

export function prependQuestToNaturalFeedSession(sessionKey: string, quest: Quest) {
    const session = sessions.get(sessionKey)
    if (!session) return

    const descriptor = buildNaturalFeedQuestDescriptor(quest)
    const firstPage = session.pages.get(1) || []
    const nextFirstPage = [
        descriptor,
        ...firstPage.filter(existing => existing.rowKey !== descriptor.rowKey),
    ]
    session.pages.set(1, nextFirstPage)
    trimSessionPages(session)
    const rows = flattenSessionRows(session, session.snapshot.pinnedQuest)
    session.lastAccessedAt = nextAccessSequence()
    session.snapshot = {
        atCapacity: rows.length >= NATURAL_FEED_MAX_ROWS_PER_SESSION,
        pinnedQuest: session.snapshot.pinnedQuest,
        revision: session.snapshot.revision + 1,
        rows,
    }
    notifySession(sessionKey)
}

export function getNaturalFeedSessionSnapshot(sessionKey: string | null | undefined) {
    if (!sessionKey) return EMPTY_SNAPSHOT
    return sessions.get(sessionKey)?.snapshot || EMPTY_SNAPSHOT
}

export function subscribeToNaturalFeedSession(
    sessionKey: string | null | undefined,
    listener: () => void,
) {
    if (!sessionKey) return () => {}

    let listeners = listenersBySession.get(sessionKey)
    if (!listeners) {
        listeners = new Set()
        listenersBySession.set(sessionKey, listeners)
    }
    listeners.add(listener)

    return () => {
        listeners?.delete(listener)
        if (listeners?.size === 0) {
            listenersBySession.delete(sessionKey)
            evictOldSessions()
            queueMicrotask(() => evictOldSessions())
        }
    }
}

export function readNaturalFeedSegmentGeometry(
    sessionKey: string,
    segmentKey: string,
    layoutSignature: string,
): number | null {
    const session = sessions.get(sessionKey)
    const geometry = session?.geometry.get(segmentKey)
    if (!geometry || geometry.layoutSignature !== layoutSignature) return null
    session?.geometry.delete(segmentKey)
    session?.geometry.set(segmentKey, geometry)
    return geometry.height
}

export function rememberNaturalFeedSegmentGeometry(
    sessionKey: string,
    segmentKey: string,
    layoutSignature: string,
    height: number,
) {
    if (!Number.isFinite(height) || height <= 0) return

    const session = sessions.get(sessionKey) || createSession()
    session.lastAccessedAt = nextAccessSequence()
    session.geometry.delete(segmentKey)
    session.geometry.set(segmentKey, { height, layoutSignature })
    while (session.geometry.size > NATURAL_FEED_MAX_GEOMETRY_ENTRIES_PER_SESSION) {
        const oldestKey = session.geometry.keys().next().value
        if (oldestKey == null) break
        session.geometry.delete(oldestKey)
    }
    sessions.set(sessionKey, session)
    evictOldSessions(sessionKey)
}

export function removeQuestFromNaturalFeedSession(sessionKey: string, questId: string | number) {
    const session = sessions.get(sessionKey)
    if (!session) return

    const normalizedQuestId = String(questId)
    let changed = false
    session.pages.forEach((pageRows, page) => {
        const nextRows = pageRows.filter(
            descriptor => String(descriptor.row?.id) !== normalizedQuestId,
        )
        if (nextRows.length === pageRows.length) return
        session.pages.set(page, nextRows)
        changed = true
    })
    const pinnedQuest =
        String(session.snapshot.pinnedQuest?.row?.id) === normalizedQuestId
            ? null
            : session.snapshot.pinnedQuest
    if (pinnedQuest !== session.snapshot.pinnedQuest) changed = true
    if (!changed) return

    const rows = flattenSessionRows(session, pinnedQuest)
    session.snapshot = {
        atCapacity: rows.length >= NATURAL_FEED_MAX_ROWS_PER_SESSION,
        pinnedQuest,
        revision: session.snapshot.revision + 1,
        rows,
    }
    notifySession(sessionKey)
}

export function resetNaturalFeedSessionSnapshot(sessionKey: string) {
    const session = sessions.get(sessionKey)
    if (!session) return

    session.resetOnNextFirstPage = true
}

export function resetNaturalFeedSessionsForTests() {
    sessions.clear()
    listenersBySession.clear()
    accessSequence = 0
}
